import { useCallback, useEffect, useMemo, useState } from "react";
import { salesApi, type SalesListKey, type SalesMeta, type SalesOption } from "../../api/salesApi";
import { errorMessage } from "./salesUi";

/* One load of GET /sales/meta shared by every sales screen; the settings screen calls reload() after a save. */
let cache: SalesMeta | null = null;
let inflight: Promise<SalesMeta> | null = null;
const listeners = new Set<(m: SalesMeta) => void>();

function fetchMeta(force = false): Promise<SalesMeta> {
  if (cache && !force) return Promise.resolve(cache);
  if (inflight && !force) return inflight;
  const p = salesApi.meta().then((m) => {
    cache = m;
    inflight = null;
    listeners.forEach((l) => l(m));
    return m;
  });
  p.catch(() => {
    inflight = null;
  });
  inflight = p;
  return p;
}

export type SalesMetaApi = {
  meta: SalesMeta | null;
  error: string | null;
  reload: () => Promise<void>;
  /** active options of a list, as Select options */
  optionsOf: (listKey: SalesListKey, opts?: { all?: boolean }) => Array<{ value: string; label: string }>;
  /** label of a stored key (falls back to the key with "_" → space) */
  label: (listKey: SalesListKey, key: string | null | undefined) => string;
  productOptions: Array<{ value: string; label: string }>;
  repOptions: Array<{ value: string; label: string }>;
};

export function useSalesMeta(): SalesMetaApi {
  const [meta, setMeta] = useState<SalesMeta | null>(cache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listeners.add(setMeta);
    fetchMeta()
      .then(setMeta)
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את הגדרות המכירות")));
    return () => {
      listeners.delete(setMeta);
    };
  }, []);

  const reload = useCallback(async () => {
    try {
      await fetchMeta(true);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "לא הצלחנו לטעון את הגדרות המכירות"));
    }
  }, []);

  return useMemo(() => {
    const list = (k: SalesListKey): SalesOption[] => [...(meta?.options?.[k] ?? [])].sort((a, b) => a.order - b.order);
    return {
      meta,
      error,
      reload,
      optionsOf: (k, opts) =>
        list(k)
          .filter((o) => opts?.all || o.active)
          .map((o) => ({ value: o.key, label: o.label })),
      label: (k, key) => {
        if (!key) return "";
        return list(k).find((o) => o.key === key)?.label ?? key.replace(/_/g, " ");
      },
      productOptions: (meta?.products ?? [])
        .filter((p) => p.active)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "he"))
        .map((p) => ({ value: p.id, label: p.groupName ? `${p.name} · ${p.groupName}` : p.name })),
      repOptions: (meta?.reps ?? []).filter((r) => r.active).map((r) => ({ value: r.id, label: r.name })),
    };
  }, [meta, error, reload]);
}
